//PrintMap.tsx =================================================
import "../Util/Prototypes";

import * as GraLay from "../Util/GraLay";
import * as MainMap from "../MainMap";
import * as Print from "./Print";
import * as PrintDataTable from "./PrintDataTable";
import * as PrintMap from "./PrintMap";
import * as PrintUI from "./PrintUI";
import * as Search from "../Search/Search";
import * as Settings from "../Settings";
import * as Spinner from "../Util/Spinner";
import * as Util from "../Util/Util";
import * as Util_App from "../Util/Util_App";

import React from "react";

import Extent from "@arcgis/core/geometry/Extent";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import Polyline from "@arcgis/core/geometry/Polyline";

//import GeoTIFF, { writeArrayBuffer } from 'geotiff';

export const legendWidth = 2; //Inches.
export let mapScale: number;
export let mapScaleText: string;
export let imageHeight: string;
export let busy = false;

let printAreaOutline: __esri.Polyline;
let xMinPrev = "";
let viewWidthPrev = "";

let imageWidth: string;
let map_url: string;

let _wdPix: number;
let _htPix: number;
let _wdInch: number;
let _htInch: number;
let _dpi: number;

//Setters.
export function setBusy(busyBee: boolean) {
   busy = busyBee;
}

//Clear this tool.
export function clearTool() {
   GraLay.temp.removeAll();
}

//Compute print sizes and map extent.
export function setPrintParameters(zoomTo: boolean = true) {
   //Util.errMsg("Setting print parameters");

   setPrintSizes();
   setMapExtent(zoomTo);

   //   if (PrintUI.isStandardScale) {
   setMapScaleText();
   //   }
}

//Compute print sizes.
function setPrintSizes() {
   let wd: number;
   let ht: number;

   //Overall size of div element to be printed (inches and points).
   wd = PrintUI.pageWidth - (Settings.print.margin * 2);
   ht = PrintUI.pageHeight - (Settings.print.margin * 2); // was minus 2; //Subtract top and bottom margins.

   Print.setPageWidth(inchToPt(wd));
   Print.setPageHeight(inchToPt(ht));

   //Legend width adjustment (inches).
   if (PrintUI.includeLegend) {
      wd -= PrintMap.legendWidth;
   }

   //Data table height adjustment.
   let dataGridHt: number = 0;
   const numSel: number = Search.getActiveLayerNumRec();

   if (PrintUI.includeDataTable && numSel > 0) {
      PrintDataTable.buildLableValueList();

      if (numSel === 1) {  //Single feature selected.
         dataGridHt = PrintDataTable.numRow * 0.24; //Custom! Reduce from .28 to .14 as needed to get a taller map.

      } else { //Multiple features selected.
         const lay: Search.iLayer = Search.layers[Search.activeLayerIndex];
         dataGridHt = PrintDataTable.numRow * ((lay.spec.headingFields.length * 0.14) + 0.15);  //Custom! Reduce for taller map.
      }

      if (dataGridHt >= (ht - 2)) {
         ht = 2;
      } else {
         ht -= dataGridHt;
      }
   }

   //Subtract the bottom marginalia height.
   ht -= 0.75;

   //Set size variables.
   _wdInch = wd;
   _htInch = ht;

   _dpi = Settings.print.dpi;
   if ((_wdInch * _htInch) > 125) {
      _dpi = 192; //Don't allow high dpi for large prints.
   } else if ((_wdInch * _htInch) > 250) {
      _dpi = 120; //Don't allow high dpi for large prints.
   }

   _wdPix = _wdInch * _dpi;
   _htPix = _htInch * _dpi;

   if (MainMap.activeView.spatialReference) {
      if (MainMap.activeView.spatialReference.isWebMercator) {
         _wdPix *= Settings.mercFactor;
         _htPix *= Settings.mercFactor;
      }
   }

   imageWidth = inchToPt(_wdInch);  //map img element width (points)
   imageHeight = inchToPt(_htInch); //map img element height (points)
}

//Compute map extent.
function setMapExtent(zoomTo: boolean) {
   const activeView = MainMap.activeView as __esri.MapView;
   let mapScaleFeetPerInch: number;

   //Use predefined scale from dropdown list.
   if (PrintUI.isStandardScale) {
      mapScaleFeetPerInch = PrintUI.mapScaleFeetPerInch;
      mapScale = mapScaleFeetPerInch * 12;
      Util.logMsg("Standard mapScale: " + mapScale.toFixed(0) + " mapScaleFeetPerInch: " + mapScaleFeetPerInch.toFixed(0));

   } else { //Initialize scale to current map extent (Default).
      mapScaleFeetPerInch = activeView.scale / 12;
      mapScale = activeView.scale; // Multiply to reduce value to zoom in a bit.
      Util.logMsg("Current mapScale: " + mapScale.toFixed(0) + " mapScaleFeetPerInch: " + mapScaleFeetPerInch.toFixed(0));
   }
   //Util.errMsg("Current map width pix: " + activeView.width.toFixed(0) + " height pix: " + activeView.height.toFixed(0));

   //Set map extent for print page.  The map view extent values honor the left panel padding!
   const dx: number = activeView.extent.xmax - activeView.extent.xmin;
   const dy: number = activeView.extent.ymax - activeView.extent.ymin;

   const xCen: number = (dx / 2) + activeView.extent.xmin; // Map view extent honors the left panel padding!
   const yCen: number = (dy / 2) + activeView.extent.ymin;

   mapScaleFeetPerInch = Util_App.feetToMeters(mapScaleFeetPerInch); //For Mercator.

   let xHalf: number = mapScaleFeetPerInch * ((_wdInch + 0.1) / 2); //Add fudge so print border frame does not print.
   let yHalf: number = mapScaleFeetPerInch * ((_htInch + 0.1) / 2);

   if (activeView.spatialReference && activeView.spatialReference.isWebMercator) {
      xHalf *= Settings.mercFactor;
      yHalf *= Settings.mercFactor;
   }

   let xMin: number = xCen - xHalf;
   let yMin: number = yCen - yHalf;

   let xMax: number = xCen + xHalf;
   let yMax: number = yCen + yHalf;

   let mbr = new Extent({ xmin: xMin, ymin: yMin, xmax: xMax, ymax: yMax, spatialReference: activeView.spatialReference });

   // Process current non-standard scale.
   if (!PrintUI.isStandardScale) {
      //Util.errMsg("View xMin: " + activeView.extent.xmin.toFixed(0) + " yMin: " + activeView.extent.ymin.toFixed(0) + " xMax: " + activeView.extent.xmax.toFixed(0) + " yMax: " + activeView.extent.ymax.toFixed(0));
      //Util.errMsg("MBR  xMin: " + xMin.toFixed(0) + " yMin: " + yMin.toFixed(0) + " xMax: " + xMax.toFixed(0) + " yMax: " + yMax.toFixed(0));

      const wPrint = xMax - xMin;
      const hPrint = yMax - yMin;
      const wView = activeView.extent.xmax - activeView.extent.xmin;
      const hView = activeView.extent.ymax - activeView.extent.ymin;

      const wScaleFactor = wView / wPrint;
      const hScaleFactor = hView / hPrint;

      const scaleFactor = Math.min(wScaleFactor, hScaleFactor);

      const wNew = wPrint * scaleFactor;
      const hNew = hPrint * scaleFactor;

      xHalf = wNew / 2;
      yHalf = hNew / 2;

      xMin = xCen - xHalf;
      yMin = yCen - yHalf;
      xMax = xCen + xHalf;
      yMax = yCen + yHalf;

      //Util.errMsg("xHalf ft.: " + xHalf.toFixed(0) + " yHalf ft.: " + yHalf.toFixed(0));
      //Util.errMsg("xCen: " + xCen.toFixed(0) + " yCen: " + yCen.toFixed(0));
      //Util.errMsg("MBR  xMin: " + xMin.toFixed(0) + " yMin: " + yMin.toFixed(0) + " xMax: " + xMax.toFixed(0) + " yMax: " + yMax.toFixed(0));

      mbr = new Extent({ xmin: xMin, ymin: yMin, xmax: xMax, ymax: yMax, spatialReference: activeView.spatialReference });
   }

   //Create print area graphic.
   const paths = [[[xMin, yMin], [xMin, yMax], [xMax, yMax], [xMax, yMin], [xMin, yMin]]];
   printAreaOutline = new Polyline({ hasZ: false, hasM: false, paths: paths, spatialReference: activeView.spatialReference });

   //Rotate the print graphic back to zero degrees.
   if (MainMap.mapView.rotation !== 0 && Util_App.geometryEngine) {
      printAreaOutline = Util_App.geometryEngine.rotate(printAreaOutline, MainMap.mapView.rotation) as __esri.Polyline;
   }

   //Set symbology.
   const sym = { type: "simple-line", color: [255, 200, 30, 0.7], width: 5 };
   const gra = new Graphic({ geometry: printAreaOutline, symbol: sym });

   GraLay.temp.removeAll();

   if (MainMap.view2D) {
      GraLay.temp.add(gra);
   }

   //Zoom to print extent when map scale dropdown changed.
   if (zoomTo && PrintUI.isStandardScale) {
      zoomTo = false;

      if (mbr.extent.xmin.toFixed(0) !== xMinPrev || activeView.width.toFixed(0) !== viewWidthPrev) {
         xMinPrev = mbr.extent.xmin.toFixed(0);
         viewWidthPrev = activeView.width.toFixed(0);

         //Util.errMsg("Zooming to mbr");
         activeView.goTo(mbr, Settings.animateOptions);
      }
   }
}

//Get URL of map image to be printed.  Print map step 2 of 4.
export function getMapURL(exportMap: boolean = false, processGeoRef: boolean = false): void {
   let fileName: string;

   if (MainMap.view2D) { // 2D screenshot.
      //Util.errMsg("MapView.rotation: " + MainMap.mapView.rotation.toFixed(0) + " ======================");

      //Util.errMsg("MapView Pad Left: " + MainMap.activeView.padding.left.toString() + " bot: " +
      //MainMap.activeView.padding.bottom.toString(), MainMap.activeView.padding);

      //======================================================
      //Util.errMsg("MapView xMin: " + MainMap.mapView.extent.xmin.toFixed(0) +
      //   " yMin: " + MainMap.mapView.extent.ymin.toFixed(0) +
      //   " xMax: " + MainMap.mapView.extent.xmax.toFixed(0) +
      //   " yMax: " + MainMap.mapView.extent.ymax.toFixed(0) +
      //   " Width pix: " + MainMap.mapView.width.toFixed(0) +
      //   " Height pix: " + MainMap.mapView.height.toFixed(0));

      //Util.errMsg("_htPix: " + _htPix.toString() + " _wdPix: " + _wdPix.toString());

      //let lower_left_view = MainMap.activeView.toScreen(new Point({
      //   x: MainMap.mapView.extent.xmin,
      //   y: MainMap.mapView.extent.ymin,
      //   spatialReference: MainMap.activeView.spatialReference
      //}));

      //let upper_right_view = MainMap.activeView.toScreen(new Point({
      //   x: MainMap.mapView.extent.xmax,
      //   y: MainMap.mapView.extent.ymax,
      //   spatialReference: MainMap.activeView.spatialReference
      //}));

      //Util.errMsg("MapView xMin pix: " + lower_left_view.x.toFixed(0) + //Changed after rotation ***
      //   " yMin pix: " + lower_left_view.y.toFixed(0) +
      //   " xMax pix: " + upper_right_view.x.toFixed(0) +
      //   " yMax pix: " + upper_right_view.y.toFixed(0));
      //======================================================
      //Util.errMsg("Print Area xMin: " + printAreaOutline.getPoint(0,0).x.toFixed(0) +
      //   " yMin: " + printAreaOutline.getPoint(0, 0).y.toFixed(0) +
      //   " xMax: " + printAreaOutline.getPoint(0, 2).x.toFixed(0) +
      //   " yMax: " + printAreaOutline.getPoint(0, 2).y.toFixed(0));

      const lower_left_print_area = MainMap.mapView.toScreen(new Point({ x: printAreaOutline.getPoint(0, 0).x, y: printAreaOutline.getPoint(0, 0).y, spatialReference: MainMap.activeView.spatialReference }));
      const upper_right_print_area = MainMap.mapView.toScreen(new Point({ x: printAreaOutline.getPoint(0, 2).x, y: printAreaOutline.getPoint(0, 2).y, spatialReference: MainMap.activeView.spatialReference }));

      //Util.errMsg("Print Area Pix xMin: " + lower_left_print_area.x.toFixed(0) +
      //   " yMin: " + upper_right_print_area.y.toFixed(0) +
      //   " xMax: " + printAreaOutline.extent.xmax.toFixed(0) +
      //   " yMax: " + printAreaOutline.extent.ymax.toFixed(0) +
      //   " Width pix: " + printAreaOutline.extent.width.toFixed(0) +
      //   " Height pix: " + printAreaOutline.extent.height.toFixed(0));

      const width_print_area = upper_right_print_area.x - lower_left_print_area.x;
      const height_print_area = lower_left_print_area.y - upper_right_print_area.y;

      const widthTimes2_pix = width_print_area * 2; // This doubles the resolution of the screenshot somehow.
      const heightTimes2_pix = height_print_area * 2;

      //Util.errMsg("widthTimes2_pix: " + widthTimes2_pix.toFixed(0) + " heightTimes2_pix: " + heightTimes2_pix.toFixed(0));

      // Note - Origin for pixel coordinates is upper left.
      const x_origin_pix = lower_left_print_area.x - MainMap.mapView.padding.left; //Remove the padding caused by the left panel print menu.
      const y_origin_pix = upper_right_print_area.y;

      //=====================================================
      //height = Math.abs(height);
      //width = Math.abs(width);
      //lower_left.x = 0;
      //upper_right.y = 35;

      //Util.errMsg("View xMin: " + MainMap.mapView.extent.xmin.toFixed(0) +
      //   " yMin: " + MainMap.mapView.extent.ymin.toFixed(0) +
      //   " xMax: " + MainMap.mapView.extent.xmax.toFixed(0) +
      //   " yMax: " + MainMap.mapView.extent.ymax.toFixed(0));
      //Util.errMsg("lower_left.x: " + lower_left.x.toFixed(0) + " upper_right.y: " + upper_right.y.toFixed(0));
      //=====================================================

      //Process download world file.
      if (processGeoRef) {
         //======================================================
         //const a = width / widthTimes2_pix; // Width of a pixel in map units.
         //const d = 0;
         //const b = 0;
         //const e = - height / heightTimes2_pix; // Height of a pixel in map units. (must be negative)
         //const c = xMin + (a / 2); // x map coordinate of center of upper left pixel.
         //const f = yMax + (e / 2); // y map coordinate of center of upper left pixel.
         //======================================================
         //let angle = 90 - MainMap.mapView.rotation;
         //if (angle < 0) {
         //   angle += 360;
         //}
         //Util.errMsg("Angle MapView: " + MainMap.mapView.rotation);

         let angle = - MainMap.mapView.rotation;
         if (angle < 0) {
            angle += 360;
         }
         //Util.errMsg("Angle World File: " + angle);

         angle = angle * (Math.PI / 180); // Convert to radians.
         //Util.errMsg("Cos(angle): " + Math.cos(angle).toFixed(5));
         //Util.errMsg("Sin(angle): " + Math.sin(angle).toFixed(5));

         const mx = MainMap.mapView.resolution / 2; // width / widthTimes2_pix;     // Width of a pixel in map units.
         const my = -mx; // - height / heightTimes2_pix; // Height of a pixel in map units. (must be negative)

         //Util.errMsg("mx: " + mx.toFixed(5) + " my: " + my.toFixed(5));

         const a = mx * Math.cos(angle); // Rotated width of a pixel in map units.
         const d = my * Math.sin(angle);

         //const b = my * (Math.cos(angle) - Math.sin(angle)); // Hosed at 0 degrees (should be zero)
         const b = d;

         //const e = my * (Math.sin(angle) + Math.cos(angle)); // Rotated height of a pixel in map units.
         const e = -a;

         //Compute the origin translation factor.
         const mapCenterX = widthTimes2_pix / 2;
         const mapCenterY = heightTimes2_pix / 2;

         const centerX = MainMap.mapView.center.x;
         const centerY = MainMap.mapView.center.y;

         const c = centerX - (a * mapCenterX + b * mapCenterY);
         const f = centerY - (d * mapCenterX + e * mapCenterY);

         const worldFileContent = `${a.toFixed(12)}\n${d.toFixed(12)}\n${b.toFixed(12)}\n${e.toFixed(12)}\n${c.toFixed(12)}\n${f.toFixed(12)}\n`;

         //Compute file name.
         let worldFileType = "jgw";
         if (PrintUI.exportImageFormat === "png") {
            worldFileType = "pgw";
         }
         fileName = "Export Map." + worldFileType;

         if (PrintUI.mapTitle.trim() !== "") {
            fileName = PrintUI.mapTitle.trim() + "." + worldFileType;
         }

         Util.downloadTextFile(fileName, worldFileContent);

      } else { //Process print 2D and export 2D.
         const options_2D: __esri.MapViewTakeScreenshotOptions = {
            area: { x: x_origin_pix, y: y_origin_pix, width: width_print_area, height: height_print_area },
            width: widthTimes2_pix,
            height: heightTimes2_pix,
            format: PrintUI.exportImageFormat as PrintUI.exportFormatType2D_Screenshot
         };

         MainMap.activeView.takeScreenshot(options_2D).then(function (results) {
            setPrintParameters(false); //So the yellow print area outline gets added back.

            if (exportMap) {
               fileName = "Export Map." + PrintUI.exportImageFormat;

               if (PrintUI.mapTitle.trim() !== "") {
                  fileName = PrintUI.mapTitle.trim() + "." + PrintUI.exportImageFormat;
               }

               Util.downloadImage(fileName, results.dataUrl);

            } else {
               map_url = results.dataUrl;
               Print.render();
            }
         });
      }

   } else { // 3D screenshot.
      const options_3D: __esri.SceneViewTakeScreenshotOptions = {
         height: _htPix,
         width: _wdPix,
      };

      MainMap.activeView.takeScreenshot(options_3D).then(function (results) {
         if (exportMap) {
            fileName = "Export Map 3D." + PrintUI.exportImageFormat;

            if (PrintUI.mapTitle.trim() !== "") {
               fileName = PrintUI.mapTitle.trim() + "." + PrintUI.exportImageFormat;
            }

            Util.downloadImage(fileName, results.dataUrl);

         } else {
            map_url = results.dataUrl;
            Print.render();
         }
      });
   }
}

function setMapScaleText() {
   let scale: number = mapScale / 12;
   let scaleTxt: string = scale.toFixed();
   let units: string = "feet";

   if (scale >= 2640) {
      scale = scale / 5280;
      scaleTxt = scale.toFixed(1);
      units = "miles";
      if (scale <= 1) {
         units = "mile";
      }
   }

   mapScaleText = "1 inch = " + scaleTxt + " " + units;
}

//Inch to point.
function inchToPt(inch: number): string {
   return (inch * 72) + "pt";
}

//Hack to print vector tile layers in black for light colored basemaps because the printing service does not support this.
export function toggleBlackParcelLines(visible: boolean) {
   for (const layer of MainMap.map.allLayers) {
      if (layer.id === ".Parcels Printing Black") { //Only used if exporting with the print task.
         let found = false;
         for (const darkWord of Settings.darkBasemapWords) {
            if (MainMap.map.basemap.id.Contains(darkWord)) {
               //Util.errMsg("MainMap.map.basemap.id: " + MainMap.map.basemap.id);
               found = true;
               break;
            }
         }
         if (!found) {
            layer.visible = !visible; //Toggle the black parcel lines vector tile layer.
         }
      }
   }
}

//Image loaded event.
function imageLoaded() {
   Spinner.hide();
   busy = false;

   if (PrintUI.includeLegend) {
      setTimeout(function () { window.print(); }, 500); //Wait for legend to render for 3D mode.
   } else { //Print map step 4 of 4.
      window.print();
   }
}

interface iProps {
}

//Render function.
export function Render(props: iProps) {
   return (
      <img src={map_url} onLoad={imageLoaded} alt="Map" style={{ width: imageWidth, height: imageHeight, border: "2px solid gray", margin: 0, padding: 0 }} />
   );
} //end function.

//==========================================
//Create blob.
//fetch(results.dataUrl).then(response => response.blob()).then(blob => {
//   console.log('Fetch Image Blob:', blob);

//   blob.arrayBuffer().then(async blob_arrayBuffer => {
//      console.log('Array Buffer from blob:', blob_arrayBuffer);

//      const imageData = new Uint8Array(blob_arrayBuffer);
//      console.log('ImageData from Uint8Array:', imageData); //Works

//const metaData: any = {
//   width: widthTimes2_pix,
//   height: heightTimes2_pix,
//   samplesPerPixel: 1,  // Change this according to your image
//   bitsPerSample: 8,    // Change this according to your image
//   planarConfiguration: 1,
//   geoKeys: {
//      GTModelTypeGeoKey: 1, // ModelTypeProjected
//      GTRasterTypeGeoKey: 1, // RasterPixelIsArea
//      ProjectedCSTypeGeoKey: 3857 // Example: EPSG:4326
//   },
//   tiePoints: [0, 0, 0, geoTransform[0], geoTransform[3], 0],
//   //pixelScale: [geoTransform[1], geoTransform[5], 0],
//   noData: 0
//};
//const a = 0.28;
//const e = -a;

//const metaData = {
//   width: widthTimes2_pix,
//   height: heightTimes2_pix,
//   GeographicTypeGeoKey: 4326,
//      ModelPixelScale: [a, e, 0],
//      ModelTiepoint: [0, 0, 0, 11.331755000000001, 46.268645, 0],
//};

//// Create a new GeoTIFF file with georeferencing information
//const arrayBuffer = await writeArrayBuffer(imageData, metaData);

//=======================
//      const values = [
//         // First row
//         1, 2, 3, // Pixel 1 RGB
//         4, 5, 6, // Pixel 2 RGB
//         7, 8, 9, // Pixel 3 RGB

//         // Second row
//         1, 2, 3, // Pixel 4 RGB
//         4, 5, 6, // Pixel 5 RGB
//         7, 8, 9, // Pixel 6 RGB

//         // Third row
//         1, 2, 3, // Pixel 7 RGB
//         4, 5, 6, // Pixel 8 RGB
//         7, 8, 9  // Pixel 9 RGB
//      ];
//      const metadata = {
//         height: 3,
//         width: 3,
//         ProjectedCSTypeGeoKey: 3857,  // EPSG code for Web Mercator
//         ModelPixelScale: [1000, 1000, 0],  // Pixel size in projected units (meters for EPSG:3857)
//         ModelTiepoint: [0, 0, 0, 0, 0, 0],  // The upper-left corner coordinates (in projected units)
//         BitsPerSample: [8, 8, 8],  // For an RGB image, 8 bits per channel
//         SampleFormat: [1],         // 1 = unsigned int
//         SamplesPerPixel: 3,        // 3 samples per pixel (R, G, B)
//         PhotometricInterpretation: 2,  // 2 = RGB
//         //   ImageWidth: 3,  // Width of the image in pixels
//         //   ImageLength: 3, // Height of the image in pixels
//      };

//      const arrayBuffer = await writeArrayBuffer(values, metadata);                     //=======================

//      console.log('arrayBuffer from writeArrayBuffer:', arrayBuffer);

//      // Create a Blob from the GeoTIFF ArrayBuffer
//      const geoTiffBlob = new Blob([arrayBuffer], { type: 'image/tiff' });
//      const dataUrl = URL.createObjectURL(geoTiffBlob);

//      fileName = fileName.replace(".jpg", ".tif");

//      Util.downloadImage(fileName, dataUrl);

//   }).catch(error => {
//      console.error("Error converting blob to GeoTIFF:", error);
//   });

//})
//   .catch(error => {
//      console.error('Error converting data URL to Blob:', error);
//   });

//=======================

//const fetchImageData = async (dataURL) => {
//   const response = await fetch(dataURL);
//   const blob = await response.blob();
//   const arrayBuffer = await blob.arrayBuffer();
//   return new Uint8Array(arrayBuffer);
//};

//const createImageData = (uint8Array, width, height) => {
//   return new Promise((resolve) => {
//      const canvas = document.createElement('canvas');
//      canvas.width = width;
//      canvas.height = height;
//      const ctx = canvas.getContext('2d');
//      const imageData = new ImageData(width, height);

//      // Fill ImageData with the pixel data
//      for (let i = 0; i < uint8Array.length; i += 4) {
//         imageData.data[i] = uint8Array[i];       // Red
//         imageData.data[i + 1] = uint8Array[i + 1]; // Green
//         imageData.data[i + 2] = uint8Array[i + 2]; // Blue
//         imageData.data[i + 3] = 255; // Set alpha to 255 (fully opaque)
//      }

//      ctx.putImageData(imageData, 0, 0);
//      resolve(imageData);
//   });
//};

//// Function to convert ImageData to GeoTIFF
//const convertJPEGDataURLToGeoTIFF = async (dataURL, geoKeys, outputFilePath) => {
//   try {
//      // Fetch image data as Uint8Array
//      const uint8Array = await fetchImageData(dataURL);

//      // Assuming you know the width and height of the image
//      const width = 800;  // Replace with actual width
//      const height = 600; // Replace with actual height

//      // Create ImageData from Uint8Array
//      const imageData = await createImageData(uint8Array, width, height);

//      const { data } = imageData;

//      // Create GeoTIFF tags (You need to provide the correct GeoTIFF tags here)
//      const geoTiffTags = {
//         ModelTiepoint: geoKeys.ModelTiepoint || [0, 0, 0, 0, 0, 0],
//         ModelPixelScale: geoKeys.ModelPixelScale || [1, 1, 0],
//         GTModelTypeGeoKey: geoKeys.GTModelTypeGeoKey || 1,
//         GTRasterTypeGeoKey: geoKeys.GTRasterTypeGeoKey || 1,
//         GeographicTypeGeoKey: geoKeys.GeographicTypeGeoKey || 4326,
//      };

//      // Prepare raster data
//      const rasterData = [
//         new Uint8Array(width * height),
//         new Uint8Array(width * height),
//         new Uint8Array(width * height),
//      ];

//      // Fill the raster data arrays with RGB values from the ImageData
//      for (let i = 0, j = 0; i < data.length; i += 4, j++) {
//         rasterData[0][j] = data[i]; // Red
//         rasterData[1][j] = data[i + 1]; // Green
//         rasterData[2][j] = data[i + 2]; // Blue
//      }

//      // Create GeoTIFF file
//      const geoTiff = writeArrayBuffer(
//         [{
//            width: width,
//            height: height,
//            data: rasterData,
//            samplesPerPixel: 3,
//            bitsPerSample: [8, 8, 8], // 24-bit color image: 8 bits per channel (R, G, B)
//            interleave: true,
//         }],
//         {
//            geoKeys: geoTiffTags,
//         }
//      );

//      console.log(`GeoTIFF saved to ${outputFilePath}`);
//   } catch (err) {
//      console.error("Error converting JPEG Data URL to GeoTIFF:", err);
//   }
//};
//==========================================
